import { getReportFooterText } from '../reportFooter';
export interface ChangeDocumentMemberInfo {
  name: string;
  ssan?: string | null;
  memberType?: string | null;
  rank?: string | null;
  org?: string | null;
  section?: string | null;
  phone?: string | null;
  billetDetail?: string | null;
  dateOfChange?: string | null;
}

export interface ChangeDocumentItem {
  lin?: string | null;
  nsn?: string | null;
  nomenclature: string;
  quantity?: number | null;
  qtyOnHand?: number | null;
  unitCost?: number | null;
  totalCost?: number | null;
  issueTurnInDx?: string | null;
  recoverableFlag?: string | null;
  thrPhr?: string | null;
  dueBackDate?: string | null;
}

export interface ChangeDocumentTemplateOptions {
  personLabels?: import('@/hooks/usePersonLabels').PersonLabels;
  generatedAt: Date;
  member: ChangeDocumentMemberInfo;
  items: ChangeDocumentItem[];
}

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const sanitizeText = (value: string | null | undefined): string => {
  const trimmed = value?.toString().trim();
  if (!trimmed) {
    return '—';
  }
  return escapeHtml(trimmed);
};

const formatDate = (value: string | null | undefined): string => {
  if (!value) {
    return '—';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return sanitizeText(value);
  }
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
};

const formatDateTime = (value: Date): string =>
  new Intl.DateTimeFormat('en-US', {
    dateStyle: 'long',
    timeStyle: 'short',
  }).format(value);

const formatNumber = (value: number | null | undefined): string => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return '—';
  }
  return value.toLocaleString('en-US', { maximumFractionDigits: 0 });
};

const formatCurrency = (value: number | null | undefined): string => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return '—';
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  }).format(value);
};

const buildItemsTable = (items: ChangeDocumentItem[]): string => {
  if (!items.length) {
    return `
      <tr>
        <td colspan="11" class="empty-row">
          No changes matched the selected filters.
        </td>
      </tr>
    `;
  }

  return items
    .map(
      (item) => `
        <tr>
          <td>${sanitizeText(item.lin)}</td>
          <td>${sanitizeText(item.nsn)}</td>
          <td>${sanitizeText(item.nomenclature)}</td>
          <td class="numeric">${formatNumber(item.quantity ?? null)}</td>
          <td class="numeric">${formatNumber(item.qtyOnHand ?? null)}</td>
          <td class="numeric">${formatCurrency(item.unitCost ?? null)}</td>
          <td class="numeric">${formatCurrency(item.totalCost ?? null)}</td>
          <td>${sanitizeText(item.issueTurnInDx)}</td>
          <td>${sanitizeText(item.recoverableFlag)}</td>
          <td>${sanitizeText(item.thrPhr)}</td>
          <td>${formatDate(item.dueBackDate ?? null)}</td>
        </tr>
      `,
    )
    .join('');
};

export const buildChangeDocumentReportHtml = ({
  generatedAt,
  member,
  items,
}: ChangeDocumentTemplateOptions): string => {
  const generatedLabel = formatDateTime(generatedAt);
  const itemsTable = buildItemsTable(items);

  return `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <title>Change Document</title>
    <style>
      :root {
        color-scheme: light;
      }

      * {
        box-sizing: border-box;
      }

      body {
        margin: 16px;
        font-family: 'Roboto', 'Helvetica Neue', Arial, sans-serif;
        color: #15263c;
        background: #ffffff;
      }

      header {
        margin-bottom: 16px;
      }

      .brand {
        font-size: 14px;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: #3a82f6;
        font-weight: 600;
      }

      .title {
        font-size: 28px;
        font-weight: 700;
        margin: 0;
        text-align: center;
      }

      .generated {
        margin-top: 6px;
        font-size: 12px;
        color: #5c6f85;
        text-align: right;
      }

      .info-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
        gap: 8px 16px;
        margin-top: 16px;
      }

      .info-label {
        font-size: 12px;
        font-weight: 600;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        color: #5c6f85;
        margin-bottom: 2px;
      }

      .info-value {
        font-size: 14px;
        color: #15263c;
      }

      table {
        width: 100%;
        border-collapse: collapse;
        margin-top: 16px;
        border: 1px solid #d7e3f4;
      }

      thead th {
        background: #e9f1ff;
        text-align: left;
        font-size: 12px;
        letter-spacing: 0.05em;
        text-transform: uppercase;
        padding: 10px 12px;
        color: #4a5c72;
        border-bottom: 1px solid #d7e3f4;
      }

      tbody td {
        padding: 10px 12px;
        border-bottom: 1px solid #edf3fc;
        font-size: 14px;
        color: #1f2f45;
      }

      tbody tr:last-child td {
        border-bottom: none;
      }

      tbody tr:nth-child(even) td {
        background: #f9fbff;
      }

      .numeric {
        text-align: right;
        font-variant-numeric: tabular-nums;
      }

      .empty-row {
        text-align: center;
        font-style: italic;
        color: #6b7c92;
        padding: 24px 12px;
      }

      .signature {
        margin-top: 24px;
        display: flex;
        gap: 12px;
        align-items: center;
      }

      .signature-label {
        font-size: 14px;
        font-weight: 600;
      }

      .signature-line {
        flex: 1;
        border-bottom: 1px solid #15263c;
        height: 1px;
      }

      footer {
        margin-top: 24px;
        font-size: 12px;
        color: #5c6f85;
        text-align: center;
      }

      @media print {
        body {
          margin: 8mm;
        }
      }
    </style>
  </head>
  <body>
    <header>
      <div class="brand">eQuip Transaction Suite</div>
      <h1 class="title">Change Document</h1>
      <div class="generated">Generated ${escapeHtml(generatedLabel)}</div>

      <div class="info-grid">
        <div>
          <div class="info-label">Name</div>
          <div class="info-value">${sanitizeText(member.name)}</div>
        </div>
        <div>
          <div class="info-label">SSAN</div>
          <div class="info-value">${sanitizeText(member.ssan)}</div>
        </div>
        <div>
          <div class="info-label">Type</div>
          <div class="info-value">${sanitizeText(member.memberType)}</div>
        </div>
        <div>
          <div class="info-label">Rank</div>
          <div class="info-value">${sanitizeText(member.rank)}</div>
        </div>
        <div>
          <div class="info-label">Org</div>
          <div class="info-value">${sanitizeText(member.org)}</div>
        </div>
        <div>
          <div class="info-label">Section</div>
          <div class="info-value">${sanitizeText(member.section)}</div>
        </div>
        <div>
          <div class="info-label">Phone</div>
          <div class="info-value">${sanitizeText(member.phone)}</div>
        </div>
        <div>
          <div class="info-label">Date of Change</div>
          <div class="info-value">${formatDate(member.dateOfChange ?? null)}</div>
        </div>
        <div style="grid-column: 1 / -1;">
          <div class="info-label">Billet Detail</div>
          <div class="info-value">${sanitizeText(member.billetDetail)}</div>
        </div>
      </div>
    </header>

    <section>
      <table>
        <thead>
          <tr>
            <th>LIN</th>
            <th>NSN</th>
            <th>Nomenclature</th>
            <th>Qty</th>
            <th>QTY OH</th>
            <th>Unit Cost</th>
            <th>Total Cost</th>
            <th>Issue / Turn In / DX</th>
            <th>Recoverable / NonRecoverable</th>
            <th>THR / PHR</th>
            <th>Due Back Date</th>
          </tr>
        </thead>
        <tbody>
          ${itemsTable}
        </tbody>
      </table>
    </section>

    <div class="signature">
      <div class="signature-label">Signature:</div>
      <div class="signature-line"></div>
    </div>

    <footer>
      Generated ${escapeHtml(generatedLabel)} · ${getReportFooterText()}
    </footer>
  </body>
</html>`;
};
